import { useMemo } from "react";
import { Calendar, Loader2 } from "lucide-react";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const HOURS_LABEL = ["0", "", "", "", "4", "", "", "", "8", "", "", "", "12", "", "", "", "16", "", "", "", "20", "", "", ""];

/**
 * Activity Heatmap (7 days × 24 hours). Aggregates client-side from the
 * activities array using the user's LOCAL timezone (Intl.DateTimeFormat).
 */
export default function ActivityHeatmap({ activities }) {
    const { matrix_count, matrix_points, max_count, total_activities, peak } = useMemo(() => {
        const mc = Array.from({ length: 7 }, () => Array(24).fill(0));
        const mp = Array.from({ length: 7 }, () => Array(24).fill(0));
        let total = 0, maxC = 0;
        let pk = { day: -1, hour: -1, count: 0 };
        (activities || []).forEach((a) => {
            if (!a?.created_at) return;
            const dt = new Date(a.created_at);
            if (Number.isNaN(dt.getTime())) return;
            const jsDay = dt.getDay();
            const day = jsDay === 0 ? 6 : jsDay - 1;
            const hour = dt.getHours();
            mc[day][hour] += 1;
            mp[day][hour] += Number(a.points) || 0;
            total += 1;
            if (mc[day][hour] > maxC) maxC = mc[day][hour];
            if (mc[day][hour] > pk.count) pk = { day, hour, count: mc[day][hour] };
        });
        return { matrix_count: mc, matrix_points: mp, max_count: maxC, total_activities: total, peak: pk };
    }, [activities]);

    const max = max_count || 1;
    let tzLabel = "Lokal";
    try {
        tzLabel = Intl.DateTimeFormat().resolvedOptions().timeZone || "Lokal";
    } catch { /* unsupported */ }

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="vermittler-heatmap">
            <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-5 h-5 text-[#FFCC00]" />
                <h2 className="font-display font-bold text-lg">Aktivitäts-Heatmap</h2>
            </div>
            <p className="text-xs text-gray-600 mb-4">
                Wann sind Sie am aktivsten? Hellere Felder = mehr Aktivitäten in diesem
                Zeitfenster (<span data-testid="heatmap-tz">{tzLabel}</span>).
                {peak.count > 0 && (
                    <> Spitzenzeit: <strong>{DAYS[peak.day]} {peak.hour}:00</strong> ({peak.count} {peak.count === 1 ? "Aktion" : "Aktionen"}).</>
                )}
            </p>

            {total_activities === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-sm" data-testid="heatmap-empty">
                    Noch keine Aktivitäten — los geht's mit Ihrer ersten Empfehlung!
                </div>
            ) : (
                <div className="overflow-x-auto" data-testid="heatmap-grid">
                    <table className="w-full text-[10px] font-mono-num">
                        <thead>
                            <tr>
                                <th className="text-left text-gray-400 font-normal pr-2 sticky left-0 bg-white"></th>
                                {HOURS_LABEL.map((h, i) => (
                                    <th key={i} className="text-gray-400 font-normal text-center w-6">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {matrix_count.map((row, d) => (
                                <tr key={d} data-testid={`heatmap-row-${d}`}>
                                    <td className="text-gray-500 font-bold uppercase tracking-wider pr-2 sticky left-0 bg-white">{DAYS[d]}</td>
                                    {row.map((v, h) => {
                                        const intensity = v === 0 ? 0 : (0.15 + (v / max) * 0.85);
                                        const isPeak = d === peak.day && h === peak.hour;
                                        return (
                                            <td
                                                key={h}
                                                data-testid={`heatmap-cell-${d}-${h}`}
                                                className="w-6 h-6 border border-white"
                                                style={{
                                                    backgroundColor: v === 0 ? "#F3F4F6" : `rgba(255, 204, 0, ${intensity})`,
                                                    outline: isPeak ? "1px solid #111827" : "none",
                                                }}
                                                title={`${DAYS[d]} ${h}:00 — ${v} Aktion${v === 1 ? "" : "en"} · ${matrix_points[d][h]} P`}
                                            />
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex items-center gap-2 mt-3 text-[10px] text-gray-500">
                        <span>Wenig</span>
                        {[0.15, 0.4, 0.65, 0.85, 1].map((i, idx) => (
                            <span key={idx} className="w-4 h-3 border border-white" style={{ backgroundColor: `rgba(255, 204, 0, ${i})` }} />
                        ))}
                        <span>Viel</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ActivityHeatmapSkeleton() {
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="heatmap-loading">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
    );
}
